import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import { EquipmentManufacturerService } from '../../../../../core/services/gse/equipment-manufacturer.service';
import * as ManufacturerActions from './manufacturer.actions';
import { ToastActions } from '../../../../toast/toast.actions';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class ManufacturerEffects {
    private actions$ = inject(Actions);
    private manufacturerService = inject(EquipmentManufacturerService);

    loadManufacturers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManufacturerActions.loadManufacturers),
            mergeMap(({ params }) =>
                this.manufacturerService.getManufacturers(params).pipe(
                    map((response) => ManufacturerActions.loadManufacturersSuccess({ response })),
                    catchError((error) => of(ManufacturerActions.loadManufacturersFailure({ error })))
                )
            )
        )
    );

    createManufacturer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManufacturerActions.createManufacturer),
            mergeMap(({ body }) =>
                this.manufacturerService.createManufacturer(body).pipe(
                    map((response) => ManufacturerActions.createManufacturerSuccess({ response })),
                    catchError((error) => of(ManufacturerActions.createManufacturerFailure({ error })))
                )
            )
        )
    );

    updateManufacturer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManufacturerActions.updateManufacturer),
            mergeMap(({ id, body }) =>
                this.manufacturerService.updateManufacturer(id, body).pipe(
                    map((response) => ManufacturerActions.updateManufacturerSuccess({ response })),
                    catchError((error) => of(ManufacturerActions.updateManufacturerFailure({ error })))
                )
            )
        )
    );

    deleteManufacturer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManufacturerActions.deleteManufacturer),
            mergeMap(({ id }) =>
                this.manufacturerService.deleteManufacturer(id).pipe(
                    map(() => ManufacturerActions.deleteManufacturerSuccess({ id })),
                    catchError((error) => of(ManufacturerActions.deleteManufacturerFailure({ error })))
                )
            )
        )
    );

    handleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ManufacturerActions.createManufacturerSuccess,
                    ManufacturerActions.updateManufacturerSuccess,
                    ManufacturerActions.deleteManufacturerSuccess
                ),
                map((action) => {
                    let detail = 'Operation completed successfully';
                    if (action.type === ManufacturerActions.createManufacturerSuccess.type) {
                        detail = 'Manufacturer created successfully';
                    } else if (action.type === ManufacturerActions.updateManufacturerSuccess.type) {
                        detail = 'Manufacturer updated successfully';
                    } else if (action.type === ManufacturerActions.deleteManufacturerSuccess.type) {
                        detail = 'Manufacturer deleted successfully';
                    }

                    return ToastActions.showSuccessToast({
                        summary: 'Success',
                        detail
                    });
                })
            ),
        { dispatch: true }
    );

    handleFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ManufacturerActions.loadManufacturersFailure,
                    ManufacturerActions.createManufacturerFailure,
                    ManufacturerActions.updateManufacturerFailure,
                    ManufacturerActions.deleteManufacturerFailure
                ),
                map(({ error }) => {
                    let errorMessage = 'An error occurred';
                    if (error instanceof HttpErrorResponse) {
                        errorMessage = error.error?.message || error.message || errorMessage;
                    }
                    return ToastActions.showErrorToast({
                        summary: 'Error',
                        detail: errorMessage
                    });
                })
            ),
        { dispatch: true }
    );
}
